import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Flame, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { applyTopupCoupon, createTopupCheckout } from "@/lib/wallets.functions";
import { MIN_TOPUP_USD, TOPUP_BONUS_USD } from "@/lib/wallet-constants";
import { HEAT_AED_CENTS } from "@/lib/behot";
import { tagOutboundHref } from "@/lib/outbound";
import { analytics, rememberPendingCheckout } from "@/lib/analytics";
import { useT } from "@/i18n";

/** 1 Heat = US$1.00 — presets are Heat amounts. */
const PRESETS = [5, 10, 25, 50, 100];

export function TopupPanel({
  returnTo,
  suggestUsd,
  compact = false,
  beforeCheckout,
}: {
  returnTo?: string;
  suggestUsd?: number;
  compact?: boolean;
  /** Runs right before we hand off to the card checkout (e.g. to remember the pending action). */
  beforeCheckout?: (heat: number) => void;
}) {
  const { t } = useT();
  const topupFn = useServerFn(createTopupCheckout);
  const couponFn = useServerFn(applyTopupCoupon);
  const queryClient = useQueryClient();
  const initial = suggestUsd && suggestUsd > 0 ? Math.max(MIN_TOPUP_USD, Math.ceil(suggestUsd)) : 25;
  const [heat, setHeat] = useState(String(initial));
  const [couponInput, setCouponInput] = useState("");
  const [discount, setDiscount] = useState<{ code: string; percentOff: number } | null>(null);

  const applyCoupon = useMutation({
    mutationFn: async () => {
      const code = couponInput.trim();
      if (code.length < 3) throw new Error(t("flow.topup.toast.enterCoupon"));
      return couponFn({ data: { code } });
    },
    onSuccess: (res) => {
      toast.success(res.message);
      if (res.kind === "percent") {
        setDiscount({ code: res.code, percentOff: res.percentOff });
      } else {
        setCouponInput("");
        void queryClient.invalidateQueries({ queryKey: ["wallet-overview"] });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const topup = useMutation({
    mutationFn: async (amount?: number) => {
      const value = amount ?? Number(heat);
      if (!Number.isFinite(value) || value < MIN_TOPUP_USD) {
        throw new Error(t("flow.topup.toast.minTopup", { min: MIN_TOPUP_USD }));
      }
      beforeCheckout?.(value);
      analytics.beginCheckout({ kind: "topup", valueUsd: value });
      rememberPendingCheckout("topup", value);
      return topupFn({
        data: {
          amountCents: Math.round(value * HEAT_AED_CENTS),
          origin: window.location.origin,
          ...(returnTo ? { returnTo } : {}),
          ...(discount ? { couponCode: discount.code } : {}),
        },
      });
    },
    onSuccess: (res) => {
      window.location.href = tagOutboundHref(res.url);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const heatValue = Number(heat) || 0;
  // Every top-up credits 1 extra Heat, charged at checkout.
  const creditHeat = heatValue > 0 ? heatValue + TOPUP_BONUS_USD : 0;
  const payUsd = discount ? creditHeat * (1 - discount.percentOff / 100) : creditHeat;

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((v) => (
          <Button
            key={v}
            variant={heat === String(v) ? "heat" : "outline"}
            size="sm"
            onClick={() => setHeat(String(v))}
          >
            {t("flow.topup.presetHeat", { amount: v })}
          </Button>
        ))}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("flow.topup.customHeat")}</span>
          <Input
            className="w-24"
            inputMode="numeric"
            value={heat}
            onChange={(e) => setHeat(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <TicketPercent className="size-4 text-muted-foreground" />
        <Input
          className="w-40 uppercase"
          placeholder={t("flow.topup.couponPlaceholder")}
          value={couponInput}
          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyCoupon.mutate();
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => applyCoupon.mutate()}
          disabled={applyCoupon.isPending}
        >
          {applyCoupon.isPending ? t("flow.topup.checking") : t("flow.topup.apply")}
        </Button>
        {discount && (
          <span className="text-xs font-semibold text-primary">
            {t("flow.topup.discountLabel", { code: discount.code, percent: discount.percentOff })}
            <button
              type="button"
              className="ml-2 underline text-muted-foreground"
              onClick={() => {
                setDiscount(null);
                setCouponInput("");
              }}
            >
              {t("flow.topup.remove")}
            </button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="hot" onClick={() => topup.mutate(undefined)} disabled={topup.isPending}>
          <Flame className="size-4" />
          {topup.isPending
            ? t("flow.topup.openingCheckout")
            : t("flow.topup.buyHeat", { amount: creditHeat, price: payUsd.toFixed(payUsd % 1 === 0 ? 0 : 2) })}
        </Button>
        <p className="text-xs text-muted-foreground">
          {t("flow.topup.footerNote", { min: MIN_TOPUP_USD })}
        </p>
        <p className="w-full text-xs text-muted-foreground">
          {t("flow.topup.bonusNote", { bonus: TOPUP_BONUS_USD })}
        </p>
      </div>
    </div>
  );
}
